import { useCallback, useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useNavigate, useParams } from 'react-router-dom';
import {
  archiveScholarship,
  getScholarship,
  restoreScholarship,
  updateScholarshipCards,
} from '../../../../services/scholarships';
import type { Scholarship } from '../../../../types/scholarships';
import { Button } from '../../../../components/Button';
import { ConfirmModal } from '../../../../components/ConfirmModal';
import { getSafeErrorMessage } from '../../../../utils/errorHandler';
import { ConfigureLimitsModal } from './ConfigureLimitsModal';
import { useRosterSport } from './useRosterSport';
import { DeleteScholarshipModal } from './DeleteScholarshipModal';
import { DuplicateScholarshipModal } from './DuplicateScholarshipModal';
import { NameScholarshipModal } from './NameScholarshipModal';
import { ScholarshipSummaryCard } from './ScholarshipSummaryCard';
import { SummaryCardPicker } from './SummaryCardPicker';
import { tourAnchor } from '../../../tours/tourAnchors';
import { setActiveRosterBoardKind } from '../../../tours/activeRosterBoard';
import { configureBoardLabel, isRecruitingBoard } from './boardPurpose';
import './ScholarshipDetailLayout.css';

export interface ScholarshipDetailContext {
  scholarship: Scholarship;
  refresh: () => Promise<void>;
}

const tabClassName = ({ isActive }: { isActive: boolean }) =>
  isActive
    ? 'scholarship-detail__tab scholarship-detail__tab--active'
    : 'scholarship-detail__tab';

export const ScholarshipDetailLayout = () => {
  // Budget rows name a position, and DH reads as DP for a softball program.
  const sport = useRosterSport();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [scholarship, setScholarship] = useState<Scholarship | null>(null);
  const [formAction, setFormAction] = useState<
    'rename' | 'delete' | 'configure' | 'duplicate' | null
  >(null);
  const [confirmKind, setConfirmKind] = useState<'archive' | 'restore' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);

  const refresh = useCallback(async () => {
    if (!id) return;
    setError(null);
    try {
      setScholarship(await getScholarship(id));
      setNotFound(false);
    } catch (e) {
      const errorWithStatus = e as { statusCode?: number };
      if (errorWithStatus?.statusCode === 404) {
        setNotFound(true);
        return;
      }
      setError(getSafeErrorMessage(e, 'Failed to load roster'));
    }
  }, [id]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Published for the Guided tours list, which sits outside this layout and offers different
  // tours on a recruiting board. Cleared on the way out so the list never sees a stale board.
  const boardKind = scholarship
    ? isRecruitingBoard(scholarship) ? 'recruiting' : 'roster'
    : null;
  useEffect(() => {
    setActiveRosterBoardKind(boardKind);
  }, [boardKind]);
  useEffect(() => () => setActiveRosterBoardKind(null), []);

  const handleCardsChange = async (visibleCards: string[]) => {
    if (!scholarship) return;
    const previous = scholarship;

    // Optimistic: the picker is a direct-manipulation control, and waiting on a round trip
    // makes every tick feel broken. The rollback below is what keeps that honest.
    setScholarship({ ...scholarship, visibleCards });
    setError(null);
    try {
      setScholarship(await updateScholarshipCards(scholarship.id, visibleCards));
    } catch (e) {
      // Restore the previous band rather than leaving the page showing a selection the
      // server never accepted — a silent failure here would have the coach believe their
      // layout was saved until the next reload told them otherwise.
      setScholarship(previous);
      setError(getSafeErrorMessage(e, 'Failed to update summary cards'));
    }
  };

  const handleConfirm = async () => {
    if (!scholarship || !confirmKind) return;
    try {
      await (confirmKind === 'archive' ? archiveScholarship : restoreScholarship)(scholarship.id);
      setConfirmKind(null);
      await refresh();
    } catch (e) {
      setError(getSafeErrorMessage(e, `Failed to ${confirmKind} roster`));
    }
  };

  if (notFound) {
    return (
      <div className="scholarship-detail">
        <nav className="scholarship-detail__breadcrumb">
          <Link to="/rosters">← Roster Planning</Link>
        </nav>
        <div className="scholarship-detail__error" role="alert">
          Roster not found.
        </div>
      </div>
    );
  }

  if (!scholarship) {
    return (
      <div className="scholarship-detail scholarship-detail--loading">
        Loading roster...
      </div>
    );
  }

  const context: ScholarshipDetailContext = { scholarship, refresh };
  const recruiting = isRecruitingBoard(scholarship);

  return (
    <div className="scholarship-detail">
      <nav className="scholarship-detail__breadcrumb">
        <Link to="/rosters">← Roster Planning</Link>
      </nav>

      {error && (
        <div className="scholarship-detail__error" role="alert">{error}</div>
      )}

      <header className="scholarship-detail__header">
        <div className="scholarship-detail__title-row" {...tourAnchor('rosters.title')}>
          <h1 className="scholarship-detail__title">{scholarship.name}</h1>
          {scholarship.isArchived && (
            <span className="scholarship-detail__archived-pill">Archived</span>
          )}
        </div>
        <div className="scholarship-detail__actions">
          <SummaryCardPicker scholarship={scholarship} onChange={handleCardsChange} />
          {/* Rename, not Edit — it opens the rename dialog and the only field is the name. */}
          <Button variant="secondary" onClick={() => setFormAction('rename')}>
            Rename
          </Button>
          <Button variant="secondary" onClick={() => setFormAction('duplicate')}>
            Duplicate
          </Button>
          <Button
            variant="secondary"
            onClick={() => setFormAction('configure')}
            {...tourAnchor('rosters.limits')}
          >
            {configureBoardLabel(scholarship)}
          </Button>
          <Button
            variant="secondary"
            onClick={() => setConfirmKind(scholarship.isArchived ? 'restore' : 'archive')}
          >
            {scholarship.isArchived ? 'Restore' : 'Archive'}
          </Button>
          <Button
            variant="secondary"
            className="scholarship-detail__delete-button"
            onClick={() => setFormAction('delete')}
          >
            Delete
          </Button>
        </div>
      </header>

      {/* Print region: a "Save as PDF" action on the depth chart adds
          body.scholarship-printing, and the print stylesheet reveals only this
          subtree. The summary band and tabs within it are suppressed in print,
          so the export is the diamond + position cards + pitching staff cards
          (the "Pitching Staff" heading is dropped). */}
      <div className="scholarship-detail__print-region">
      <ScholarshipSummaryCard scholarship={scholarship} />

      <nav
        className="scholarship-detail__tabs"
        aria-label="Roster sections"
        {...tourAnchor('rosters.tabs')}
      >
        <NavLink
          to="players"
          className={tabClassName}
        >
          Players
        </NavLink>
        <NavLink
          to="depth-chart"
          {...tourAnchor('rosters.depth-chart')}
          className={tabClassName}
        >
          Depth chart
        </NavLink>
        <NavLink
          to="pitching-staff"
          {...tourAnchor('rosters.pitching-tab')}
          className={tabClassName}
        >
          {recruiting ? 'Pitchers' : 'Pitching staff'}
        </NavLink>
        {/* A recruiting board has no lineups: it is a class being recruited, not a team that
            plays. The route still exists and sends a recruiting board back to Players. */}
        {!recruiting && (
          <NavLink
            to="lineups"
            {...tourAnchor('rosters.lineups-tab')}
            className={tabClassName}
          >
            Lineups
          </NavLink>
        )}
      </nav>

      <div className="scholarship-detail__body">
        <Outlet context={context} />
      </div>
      </div>

      {formAction === 'rename' && (
        <NameScholarshipModal
          scholarship={{ id: scholarship.id, name: scholarship.name }}
          onClose={() => setFormAction(null)}
          onSuccess={() => {
            setFormAction(null);
            refresh();
          }}
        />
      )}

      {formAction === 'configure' && (
        <ConfigureLimitsModal
          sport={sport}
          scholarship={scholarship}
          onClose={() => setFormAction(null)}
          onSuccess={() => {
            setFormAction(null);
            refresh();
          }}
        />
      )}

      {formAction === 'duplicate' && (
        <DuplicateScholarshipModal
          scholarship={{ id: scholarship.id, name: scholarship.name }}
          onClose={() => setFormAction(null)}
          onSuccess={(copy) => {
            setFormAction(null);
            navigate(`/rosters/${copy.id}`);
          }}
        />
      )}

      {formAction === 'delete' && (
        <DeleteScholarshipModal
          scholarship={{ id: scholarship.id, name: scholarship.name }}
          onClose={() => setFormAction(null)}
          onSuccess={() => navigate('/rosters')}
        />
      )}

      <ConfirmModal
        isOpen={confirmKind !== null}
        title={confirmKind === 'archive' ? 'Archive roster?' : 'Restore roster?'}
        message={
          confirmKind === 'archive'
            ? `Archive '${scholarship.name}'? You can restore it later from Show archived.`
            : `Restore '${scholarship.name}'?`
        }
        confirmText={confirmKind === 'archive' ? 'Archive' : 'Restore'}
        onConfirm={handleConfirm}
        onClose={() => setConfirmKind(null)}
      />
    </div>
  );
};
